import React, { useState, useEffect, useCallback } from 'react';
import { usePersistedState } from '../hooks/usePersistedState';
import { toast } from 'react-toastify';
import {
  getUsers,
  getUserStats,
  blockUser,
  unblockUser,
  deleteUser
} from '../services/users.service';

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    active: { bg: 'bg-green-500/10', text: 'text-green-400', icon: 'check_circle', label: 'Active' },
    blocked: { bg: 'bg-red-500/10', text: 'text-red-400', icon: 'block', label: 'Blocked' },
    deleted: { bg: 'bg-gray-500/10', text: 'text-gray-400', icon: 'delete', label: 'Deleted' }
  };

  const statusConfig = config[status] || config.active;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
      <span className="material-symbols-outlined text-sm">{statusConfig.icon}</span>
      {statusConfig.label}
    </span>
  );
};

// View User Modal
const ViewModal = ({ isOpen, onClose, user }) => {
  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#0a0a0f] border border-white/10 rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden animate-fade-in">
          
          {/* Header */}
          <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">User Details</h3>
            <button
              onClick={onClose}
              className="p-2 text-white/40 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              <span className="material-symbols-outlined text-xl">close</span>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-130px)] space-y-6">
            {/* User Avatar & Basic Info */}
            <div className="flex items-center gap-4">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white text-xl font-medium">
                  {user.name?.charAt(0) || 'U'}
                </div>
              )}
              <div className="flex-1">
                <p className="font-medium text-white text-lg">{user.name || 'Unknown User'}</p>
                <p className="text-sm text-white/40">{user.email}</p>
                <div className="mt-2">
                  <StatusBadge status={user.status} />
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4 text-center">
                <div className="flex items-center justify-center gap-2 text-purple-400 mb-1">
                  <span className="material-symbols-outlined text-lg">rate_review</span>
                  <span className="text-2xl font-light">{user.reviews_count || 0}</span>
                </div>
                <p className="text-xs text-white/40">Reviews</p>
              </div>
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4 text-center">
                <div className="flex items-center justify-center gap-2 text-indigo-400 mb-1">
                  <span className="material-symbols-outlined text-lg">feedback</span>
                  <span className="text-2xl font-light">{user.feedback_count || 0}</span>
                </div>
                <p className="text-xs text-white/40">Feedback</p>
              </div>
            </div>

            {/* Additional Info */}
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-white/40 text-sm">Role</span>
                <span className="text-white text-sm capitalize">{user.role || 'user'}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-white/40 text-sm">Member Since</span>
                <span className="text-white text-sm">
                  {user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  }) : 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-white/40 text-sm">User ID</span>
                <span className="text-white/60 text-sm font-mono">#{user.id}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-white/5 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/5 border border-white/10 text-white rounded-xl font-medium hover:bg-white/10 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Block Confirmation Modal
const BlockModal = ({ isOpen, onClose, onConfirm, isLoading, user }) => {
  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#0a0a0f] border border-white/10 rounded-2xl w-full max-w-sm p-6 animate-fade-in">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl text-amber-400">block</span>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">Block User</h3>
            <p className="text-white/50">
              Are you sure you want to block <span className="text-white font-medium">{user.name || user.email}</span>? 
              They will no longer be able to post reviews or feedback.
            </p>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 text-white rounded-xl font-medium hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                  Blocking...
                </>
              ) : (
                'Block User'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteModal = ({ isOpen, onClose, onConfirm, isLoading, user }) => {
  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#0a0a0f] border border-white/10 rounded-2xl w-full max-w-sm p-6 animate-fade-in">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl text-red-400">warning</span>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">Delete User Permanently</h3>
            <p className="text-white/50 mb-3">
              This will permanently delete <span className="text-white font-medium">{user.name || user.email}</span> and all their content.
            </p>
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-left">
              <p className="text-red-400 text-sm font-medium mb-1">This action will delete:</p>
              <ul className="text-red-300/80 text-sm space-y-1">
                <li className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-xs">check</span>
                  {user.reviews_count || 0} reviews
                </li>
                <li className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-xs">check</span>
                  {user.feedback_count || 0} feedback entries
                </li>
              </ul>
            </div>
            <p className="text-white/40 text-xs mt-3">This action cannot be undone.</p>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 text-white rounded-xl font-medium hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                  Deleting...
                </>
              ) : (
                'Delete Permanently'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function Users() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, blocked: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [statusFilter, setStatusFilter] = usePersistedState('users_status', '');
  const [searchTerm, setSearchTerm] = usePersistedState('users_search', '');
  const [viewModal, setViewModal] = useState({ isOpen: false, user: null });
  const [blockModal, setBlockModal] = useState({ isOpen: false, user: null });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, user: null });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (statusFilter) params.status = statusFilter;
      if (searchTerm.trim()) params.search = searchTerm.trim();

      const response = await getUsers(params);
      setUsers(response.data || []);
      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages
        }));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.limit, statusFilter, searchTerm]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await getUserStats();
      setStats(response.data || response);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleBlock = async () => {
    if (!blockModal.user) return;
    
    try {
      setActionLoading(blockModal.user.id);
      await blockUser(blockModal.user.id);
      toast.success('User blocked successfully');
      setBlockModal({ isOpen: false, user: null });
      fetchUsers();
      fetchStats();
    } catch (error) {
      console.error('Error blocking user:', error);
      toast.error(error.response?.data?.message || 'Failed to block user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnblock = async (user) => {
    try {
      setActionLoading(user.id);
      await unblockUser(user.id);
      toast.success('User unblocked successfully');
      fetchUsers();
      fetchStats();
    } catch (error) {
      console.error('Error unblocking user:', error);
      toast.error(error.response?.data?.message || 'Failed to unblock user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.user) return;

    try {
      setActionLoading(deleteModal.user.id);
      await deleteUser(deleteModal.user.id);
      toast.success('User and all their content deleted permanently');
      setDeleteModal({ isOpen: false, user: null });
      fetchUsers();
      fetchStats();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (isLoading && users.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
          <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-indigo-500 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      
      {/* ===== HEADER ===== */}
      <div>
        <h1 className="text-3xl font-extralight tracking-tight text-white">
          Users<span className="text-purple-400 font-bold">.</span>
        </h1>
        <p className="text-sm text-white/40 mt-1">Manage and moderate user accounts</p>
      </div>

      {/* ===== STATS ===== */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Users', value: stats.total || 0, icon: 'group', color: 'purple' },
          { label: 'Active', value: stats.active || 0, icon: 'check_circle', color: 'green' },
          { label: 'Blocked', value: stats.blocked || 0, icon: 'block', color: 'red' },
        ].map((stat) => (
          <div 
            key={stat.label} 
            className={`bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-5 hover:border-${stat.color}-500/30 transition-all duration-300`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl bg-${stat.color}-500/10 flex items-center justify-center`}>
                <span className={`material-symbols-outlined text-2xl text-${stat.color}-400`}>{stat.icon}</span>
              </div>
              <div>
                <p className="text-2xl font-light text-white">{stat.value}</p>
                <p className="text-xs text-white/40">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ===== FILTERS ===== */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
        <div className="relative flex-1 sm:max-w-xs">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-lg">search</span>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50 transition-all"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          className="px-4 py-2.5 bg-white/[0.03] border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50 transition-all"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="blocked">Blocked</option>
        </select>
        {(statusFilter || searchTerm) && (
          <button
            onClick={() => {
              setStatusFilter('');
              setSearchTerm('');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="px-4 py-2 text-white/60 hover:text-white hover:bg-white/5 rounded-xl font-medium transition-colors flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-sm">close</span>
            Clear
          </button>
        )}
      </div>

      {/* ===== USER BLOCKS GRID ===== */}
      <div className="space-y-4">
        <h2 className="text-sm font-medium text-white/50 uppercase tracking-wider">
          Users ({users.length}{pagination.total > 0 ? ` of ${pagination.total}` : ''})
        </h2>
        {users.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {users.map((user) => (
              <div
                key={user.id}
                className="group bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-5 hover:border-white/10 hover:bg-white/[0.04] transition-all duration-200"
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white font-medium flex-shrink-0">
                        {user.name?.charAt(0) || 'U'}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-white truncate" title={user.name || user.email}>{user.name || 'Unknown'}</p>
                      <p className="text-sm text-white/40 truncate" title={user.email}>{user.email}</p>
                      <div className="mt-1.5">
                        <StatusBadge status={user.status} />
                        {user.role === 'admin' && (
                          <span className="ml-1.5 px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded-lg">Admin</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setViewModal({ isOpen: true, user })}
                      className="p-2 text-white/40 hover:text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <span className="material-symbols-outlined text-lg">visibility</span>
                    </button>
                    {user.role !== 'admin' && (
                      <>
                        {user.status === 'active' || !user.status ? (
                          <button
                            onClick={() => setBlockModal({ isOpen: true, user })}
                            disabled={actionLoading === user.id}
                            className="p-2 text-white/40 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors disabled:opacity-50"
                            title="Block User"
                          >
                            {actionLoading === user.id ? (
                              <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                            ) : (
                              <span className="material-symbols-outlined text-lg">block</span>
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUnblock(user)}
                            disabled={actionLoading === user.id}
                            className="p-2 text-white/40 hover:text-green-400 hover:bg-green-500/10 rounded-lg transition-colors disabled:opacity-50"
                            title="Unblock User"
                          >
                            {actionLoading === user.id ? (
                              <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                            ) : (
                              <span className="material-symbols-outlined text-lg">check_circle</span>
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteModal({ isOpen: true, user })}
                          disabled={actionLoading === user.id}
                          className="p-2 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete Permanently"
                        >
                          <span className="material-symbols-outlined text-lg">delete_forever</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/5">
                  <div className="flex items-center gap-2 text-purple-400">
                    <span className="material-symbols-outlined text-base">rate_review</span>
                    <span className="text-sm">{user.reviews_count || 0} reviews</span>
                  </div>
                  <div className="flex items-center gap-2 text-indigo-400">
                    <span className="material-symbols-outlined text-base">feedback</span>
                    <span className="text-sm">{user.feedback_count || 0} feedback</span>
                  </div>
                </div>
                <p className="text-xs text-white/30 mt-3">Joined {formatDate(user.created_at)}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl text-white/20">group</span>
            </div>
            <p className="font-medium text-white mb-1">No users found</p>
            <p className="text-white/40 text-sm">
              {statusFilter || searchTerm ? 'Try changing your filters' : 'No users registered yet'}
            </p>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <p className="text-sm text-white/40">Showing {users.length} of {pagination.total} users</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1.5 text-sm bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="px-3 py-1.5 text-sm text-white/40">Page {pagination.page} of {pagination.totalPages}</span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="px-3 py-1.5 text-sm bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <ViewModal
        isOpen={viewModal.isOpen}
        onClose={() => setViewModal({ isOpen: false, user: null })}
        user={viewModal.user}
      />

      <BlockModal
        isOpen={blockModal.isOpen}
        onClose={() => setBlockModal({ isOpen: false, user: null })}
        onConfirm={handleBlock}
        isLoading={actionLoading === blockModal.user?.id}
        user={blockModal.user}
      />

      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, user: null })}
        onConfirm={handleDelete}
        isLoading={actionLoading === deleteModal.user?.id}
        user={deleteModal.user}
      />
    </div>
  );
}

export default Users;
